/**
 * Same as `===`, just as a function so it can be used as a callback
 */
export function isStrictEqual<T>(a: unknown, b: T): a is T {
  return a === b
}

/**
 * While strict equal consider `NaN` to not equal itself, it is usually
 * reasonable to consider `NaN` to equal itself
 *
 * Additionally, when dealing with {@link Date}: if you have separate instances
 * that represent the exact same date & time, they are effectively equal
 *
 * Other than the above cases, this is the same as {@link isStrictEqual}
 */
export function isMostlyStrictEqual<T>(a: unknown, b: T): a is T {
  return (
    isStrictEqual(a, b) ||
    (typeof a === 'number' && isNaN(a) && typeof b === 'number' && isNaN(b)) ||
    (a instanceof Date && b instanceof Date && a.valueOf() === b.valueOf())
  )
}

/**
 * Determines if the two given {@link Set} have the same items
 */
export function isSetEqual(
  a: ReadonlySet<unknown>,
  b: ReadonlySet<unknown>,
): boolean {
  if (a.size !== b.size) {
    // If the sets do not have the same size, they are certainly not equal
    return false
  }
  for (const val of a) {
    if (!b.has(val)) {
      // If a has something that b does not have, the sets are not equal
      return false
    }
  }
  // If they are the same size and b contains everything from a, that means they
  // have exactly the same contents
  return true
}

/**
 * Checks if two objects are deeply equal for immutable objects.
 * If the length of an array or the number of keys in an object exceeds the predefined maximum values,
 * the comparison is cut short and the objects are considered not equal.
 *
 * _Inspired by {@link https://github.com/ngrx/platform/issues/2908}_
 */
export function isDeepEqual<T>(
  a: unknown,
  b: T,
  {
    maxEqualityChecks = DEFAULT_MAX_EQUALITY_CHECKS,
  }: Readonly<isDeepEqual.Params> = {},
): a is T {
  if (a === b) {
    // If they are strictly equal, no need to do any of the below
    return true
  }
  type Check = readonly [unknown, unknown]
  const checksRequiringDeepCheck: Check[] = []
  /**
   * Adds a {@link Check} to {@link checksRequiringDeepCheck}, but only if we know the values are
   * not strictly equal
   */
  function addCheck(..._check: Check): { limitReached: boolean } {
    const [v1, v2] = _check
    if (--maxEqualityChecks < checksRequiringDeepCheck.length) {
      // If we hit our max, we can't process anymore equality checks
      return { limitReached: true }
    }

    if (!isMostlyStrictEqual(v1, v2)) {
      // We only need to do a deep equality check if they are not (mostly)
      // strictly equal
      checksRequiringDeepCheck.push(_check)
    }
    return { limitReached: false }
  }
  addCheck(a, b)
  let check: Check | undefined
  while ((check = checksRequiringDeepCheck.pop())) {
    // NOTE: we already know the values are not strictly equal because addCheck
    // prevents that, so we now only need to check deep equality
    const [v1, v2] = check
    if (
      // Only non-null objects can be checked for deep equality, so if we
      // encounter any null or non-object, then we have to rely on strict
      // equality, which already failed above
      v1 === null ||
      v2 === null ||
      typeof v1 !== 'object' ||
      typeof v2 !== 'object' ||
      // isMostlyStrictEqual already handles Dates, so if we encounter a Date
      // here, then we know that failed
      v1 instanceof Date ||
      v2 instanceof Date ||
      // While technically, you could have two objects that are instances of
      // different classes but have exactly the same properties, that is
      // unusual. So for the sake of a faster equality check, we just assume
      // they are not equal
      Object.getPrototypeOf(v1) !== Object.getPrototypeOf(v2)
    ) {
      return false
    }

    if (Array.isArray(v1)) {
      // NOTE: if v1 is an array, then they are both arrays since they have the
      // same prototype above
      if (v1.length !== (v2 as typeof v1).length) {
        // If the arrays do not have the same length, they are certainly not equal
        return false
      }
      for (let i = 0; i < v1.length; i++) {
        const { limitReached } = addCheck(v1[i], (v2 as typeof v1)[i])
        if (limitReached) {
          // We've hit the maximum number of checks and need to give up and
          // assume they are not equal
          return false
        }
      }
    }

    if (v1 instanceof Set) {
      // NOTE: if v1 is a set, then they are both sets since they have the
      // same prototype above

      // When checking sets, we need to check that everything in v1 is in v2 and
      // everything in v2 is in v1, so the number of checks is sum of the sizes
      // of v1 and v2
      maxEqualityChecks -= v1.size + (v2 as typeof v1).size
      if (
        maxEqualityChecks < checksRequiringDeepCheck.length ||
        !isSetEqual(v1, v2 as typeof v1)
      ) {
        // If we are going to run out of checks or if the sets are not equal, we
        // indicate the two original values are not equal
        return false
      }
    }

    // For plain objects, we just need to check if each property has the same
    // values. In order to do this, we first go through the properties of one
    // object, and check that the other object has equal values...
    const v1Keys = new Set<string>()
    for (const k in v1) {
      const { limitReached } = addCheck(
        v1[k as keyof typeof v1],
        v2[k as keyof typeof v2],
      )
      if (limitReached) {
        // We've hit the maximum number of checks and need to give up and
        // assume they are not equal
        return false
      }
      v1Keys.add(k)
    }
    // Then, we go through the properties of the other object, and any new
    // properties we encounter represent properties that are missing from the
    // first object. Thus, the value for those properties in the first object
    // are effectively `undefined`. So if the value for those properties in the
    // second object are not `undefined`, the objects are not equal
    for (const k in v2) {
      if (!v1Keys.has(k) && v2[k as keyof typeof v2] !== undefined) {
        return false
      }
    }
  }
  // All checks have passed, so the two values are equal
  return true
}
export namespace isDeepEqual {
  export interface Params {
    /**
     * Deep equality check can be expensive. In order to optimize, when
     * encountering large objects or lists, we stop checking at a certain point
     * and just say the two items are not equal. This determines at what point
     * we stop
     *
     * Set to {@link Infinity} to do complete deep equality check
     *
     * @default 2000
     */
    maxEqualityChecks?: number
  }
}
const DEFAULT_MAX_EQUALITY_CHECKS = 2000
